import React, { useEffect, useState, useMemo } from 'react';
import { Link2, Download, Copy, Eye, MousePointer, Users, ExternalLink, Plus, QrCode, BarChart3, Edit3, Trash2, Calendar, TrendingUp, Target, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import QRCode from 'react-qr-code';
import { supabase } from '@/lib/supabase';

// Fallback limits if PlanContext not yet hydrated
const defaultLimits = {
  id: 'free',
  label: 'Free',
  fans: 200,
  campaignsPerMonth: 1,
  smartLinks: 1,
  advancedAnalytics: false,
  automation: false,
  collaborations: false,
};
const SmartLinks = () => {
  const { user } = useAuth();
  const [error, setError] = useState(null);
  const [planId, setPlanId] = useState('free');
  const [planName, setPlanName] = useState('Free');
  const [limits, setLimits] = useState(defaultLimits);
  const [loadingPlan, setLoadingPlan] = useState(true);
  // Initial debug of plan context state
  const [destinationUrl, setDestinationUrl] = useState('');
  const [requireEmail, setRequireEmail] = useState(true);
  const [requireNumber, setRequireNumber] = useState(true);
  const [linkTitle, setLinkTitle] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [showQR, setShowQR] = useState(false);
  const [existingLinks, setExistingLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [slugToDelete, setSlugToDelete] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [newSlug, setNewSlug] = useState('');
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState(null);
  const [creating, setCreating] = useState(false);
  // Over-limit enforcement
  const [overLimitOpen, setOverLimitOpen] = useState(false);
  const [selectedForDeletion, setSelectedForDeletion] = useState([]);
  // Search & sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('created'); // created | clicks | newFans | title
  const [sortDir, setSortDir] = useState('desc'); // asc | desc

  const overLimit = typeof limits?.smartLinks === 'number' && existingLinks.length > limits.smartLinks;
  const excessCount = overLimit ? existingLinks.length - limits.smartLinks : 0;

  // Whenever links or limits change, enforce cleanup requirement
  useEffect(() => {
    if (!loadingPlan && overLimit) {
      // Preselect nothing; force user selection
      setSelectedForDeletion([]);
      setOverLimitOpen(true);
    } else {
      setOverLimitOpen(false);
    }
  }, [loadingPlan, overLimit]);


  useEffect(() => {
    let active = true;
    const loadPlan = async () => {
      if (!user?.id) {
        setPlanId('free');
        setPlanName('Free');
        setLimits(defaultLimits);
        setLoadingPlan(false);
        return;
      }
      setLoadingPlan(true);
      setError(null);
      try {
        const { data: subscription, error: subError } = await supabase
          .from('subscriptions')
          .select('plan_id')
          .eq('user_id', user.id)
          .maybeSingle();
        if (subError) throw subError;
        if (!subscription?.plan_id) {
          if (active) {
            setPlanId('free');
            setPlanName('Free');
            setLimits(defaultLimits);
          }
          return;
        }
        const { data: plan, error: planError } = await supabase
          .from('plans')
          .select('*')
          .eq('id', subscription.plan_id)
          .single();
        if (planError) throw planError;
        const name = plan?.name || 'Free';
        const lower = name.toLowerCase();
        let tierLimits = {
          id: 'free',
          label: name,
          fans: 200,
          campaignsPerMonth: 1,
          smartLinks: 1,
          advancedAnalytics: false,
          automation: false,
          collaborations: false,
        };
        if (lower.includes('growth')) {
          tierLimits = {
            ...tierLimits,
            id: 'growth',
            fans: 2500,
            campaignsPerMonth: null,
            smartLinks: 3,
            advancedAnalytics: true,
          };
        } else if (lower.includes('pro')) {
          tierLimits = {
            ...tierLimits,
            id: 'pro',
            fans: 10000,
            campaignsPerMonth: null,
            smartLinks: 10,
            advancedAnalytics: true,
            collaborations: true,
          };
        }
        if (active) {
          setPlanId(tierLimits.id);
          setPlanName(tierLimits.label);
          setLimits(tierLimits);
        }
      } catch (err) {
        console.warn('[SmartLinks] Plan load failed; defaulting to free:', err?.message);
        if (active) {
          setPlanId('free');
          setPlanName('Free');
          setLimits(defaultLimits);
          setError(err?.message || 'Unable to load plan');
        }
      } finally {
        if (active) setLoadingPlan(false);
      }
    };
    loadPlan();
    return () => { active = false; };
  }, [user?.id]);

  const fetchUserLinks = async () => {
      setLoadingLinks(true);
  const url = `https://backend.amply.buildfast.us/smart-links/user/${user.id}`;
      const res = await fetch(url);
      const data = await res.json().catch(err => { console.warn('[SmartLinks][fetchUserLinks] JSON parse failed', err); return {}; });
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Failed to load links');
      const list = Array.isArray(data.data) ? data.data : [];
      setExistingLinks(list);
      setLoadingLinks(false);
    }

  useEffect(() => {
    fetchUserLinks();
  }, [user?.id, loadingPlan, planId]);

  const deleteLink = async (slug) => {
    if (!user?.id) {
      toast.error('You need to be logged in');
      return;
    }
    try {
      setDeleting(slug);
  const delUrl = `https://backend.amply.buildfast.us/smart-links/${user.id}/${slug}`;
      const res = await fetch(delUrl, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Failed to delete');
      await fetchUserLinks();
    } catch (e) {
      toast.error('Delete failed', { description: e.message });
    } finally { setDeleting(null); }
  };

  const handleConfirmDelete = async () => {
    if (!slugToDelete) return;
    await deleteLink(slugToDelete);
    setIsConfirmOpen(false);
    setSlugToDelete(null);
  };

  const createLink = async () => {
    if (existingLinks.length >= (limits?.smartLinks ?? 1)) {
      toast.error(`Smart link limit reached (${limits.smartLinks})`, {
        description: `Your ${limits.label || 'current'} plan allows up to ${limits.smartLinks} smart link${limits.smartLinks === 1 ? '' : 's'}. Upgrade to create more.`
      });
      return;
    }
    if (!destinationUrl || !linkTitle) {
      toast.error('Please fill in title and destination URL');
      return;
    }
    if (!user?.id) {
      toast.error('You need to be logged in to create links');
      return;
    }
    try {
      setCreating(true);
  const res = await fetch('https://backend.amply.buildfast.us/smart-links/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'https://backend.amply.buildfast.us', 'Access-Control-Allow-Methods': 'GET, OPTIONS', 'Access-Control-Allow-Headers': 'Content-Type' },
        body: JSON.stringify({
          userId: user.id,
          title: linkTitle,
            destinationUrl,
          requireEmail,
          requirePhone: requireNumber,
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Failed to create link');
      setGeneratedLink(data.url);
      setShowQR(true);
      fetchUserLinks();
      toast.success('Smart link created!', {
        description: `Your link for "${linkTitle}" is ready to share.`
      });
      setLinkTitle('');
      setDestinationUrl('');
      setRequireEmail(true);
      setRequireNumber(true);
    } catch (e) {
      toast.error('Failed to create smart link', { description: e.message });
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!', {
      description: 'The link has been copied to your clipboard.'
    });
  };

  const toSlug = (str = '') =>
    String(str)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '')

  const checkAvailability = async (candidate) => {
    const slug = toSlug(candidate);
    if (!slug) { setAvailable(null); return; }
    try {
      setChecking(true);
  const res = await fetch(`https://backend.amply.buildfast.us/smart-links/slug-available/${slug}`);
      const data = await res.json().catch(() => ({}));
      setAvailable(res.ok && data?.success ? !!data.available : null);
    } catch {
      setAvailable(null);
    } finally {
      setChecking(false);
    }
  };

  const openEdit = (link) => {
    setEditingLink(link)
    setNewSlug(link.slug || '')
    setAvailable(null)
    setIsEditOpen(true)
  }

  const saveNewSlug = async () => {
    if (!user?.id || !editingLink) return;
    const slug = toSlug(newSlug);
    if (!slug) { toast.error('Enter a valid page name'); return; }
    try {
      if (available === false) { toast.error('That page name is taken'); return; }
  const res = await fetch(`https://backend.amply.buildfast.us/smart-links/${user.id}/${editingLink.slug}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newSlug: slug })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Failed to update');
      toast.success('Page name updated');
      setIsEditOpen(false);
      setEditingLink(null);
      setNewSlug('');
      await fetchUserLinks();
    } catch (e) {
      toast.error('Update failed', { description: e.message });
    }
  };

  // Calculate total stats
  const totalClicks = existingLinks.reduce((sum, link) => sum + (link.clicks || 0), 0);
  const totalFans = existingLinks.reduce((sum, link) => sum + (link.newFans || 0), 0);
  const activeLinks = existingLinks.length;

  const toggleSelectLink = (id) => {
    setSelectedForDeletion(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const bulkDeleteSelected = async () => {
    if (!user?.id) return;
    const toDelete = existingLinks.filter(l => selectedForDeletion.includes(l.slug));
    if (toDelete.length < excessCount) {
      toast.error(`Select at least ${excessCount} link${excessCount===1?'':'s'} to delete`);
      return;
    }
    try {
      for (const link of toDelete) {
        try {
          const delUrl = `https://backend.amply.buildfast.us/smart-links/${user.id}/${link.slug}`;
          const res = await fetch(delUrl, { method: 'DELETE' });
          await res.json().catch(()=>({}));
        } catch (e) {
          console.warn('Failed deleting', link.slug, e);
        }
      }
      await fetchUserLinks();
      toast.success('Selected links deleted');
      // Re-check will auto close dialog if now within limit
    } catch (e) {
      toast.error('Bulk delete failed');
    }
  };

  // Quick-select helpers for over-limit dialog
  const selectOldest = () => {
    const sorted = [...existingLinks]
      .sort((a,b) => new Date(a.created) - new Date(b.created))
      .slice(0, excessCount)
      .map(l => l.slug);
    setSelectedForDeletion(sorted);
  };
  const selectLeastClicks = () => {
    const sorted = [...existingLinks]
      .sort((a,b) => (a.clicks||0) - (b.clicks||0))
      .slice(0, excessCount)
      .map(l => l.slug);
    setSelectedForDeletion(sorted);
  };

  // Derived processed list with search & sort for main display
  const processedLinks = useMemo(() => {
    let list = [...existingLinks];
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(l => (
        (l.title||'').toLowerCase().includes(q) ||
        (l.slug||'').toLowerCase().includes(q) ||
        (l.destination||'').toLowerCase().includes(q)
      ));
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a,b) => {
      let av, bv;
      switch (sortField) {
        case 'clicks':
          av = a.clicks || 0; bv = b.clicks || 0; break;
        case 'newFans':
          av = a.newFans || 0; bv = b.newFans || 0; break;
        case 'title':
          av = (a.title||'').toLowerCase(); bv = (b.title||'').toLowerCase(); break;
        case 'created':
        default:
          av = new Date(a.created).getTime();
          bv = new Date(b.created).getTime();
          break;
      }
      if (av === bv) return 0;
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      return (av > bv ? 1 : -1) * dir;
    });
    return list;
  }, [existingLinks, searchTerm, sortField, sortDir]);

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Over-limit mandatory cleanup dialog */}
        <Dialog open={overLimitOpen}>
          <DialogContent className="bg-white dark:bg-black max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-red-600 dark:text-red-400 flex items-center gap-2">Smart Link Limit Exceeded</DialogTitle>
              <DialogDescription className="text-gray-600 dark:text-gray-400 space-y-2">
                <p>You have <strong>{existingLinks.length}</strong> smart links but your plan limit is <strong>{limits.smartLinks}</strong>.</p>
                <p>Please select at least <strong>{excessCount}</strong> link{excessCount===1?'':'s'} to delete before continuing.</p>
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-wrap gap-2 text-xs mt-2">
              <button onClick={selectOldest} className="px-2 py-1 text-black dark:text-white rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">Select Oldest {excessCount}</button>
              <button onClick={selectLeastClicks} className="px-2 py-1 text-black dark:text-white rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">Least Clicked {excessCount}</button>
              <button onClick={()=>setSelectedForDeletion([])} className="px-2 py-1 text-black dark:text-white rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">Clear</button>
            </div>
            <div className="max-h-80 overflow-y-auto mt-4 space-y-2">
              {existingLinks.map(link => (
                <div key={link.id} className={`flex items-center justify-between p-3 rounded-lg border text-sm ${selectedForDeletion.includes(link.slug) ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700'}` }>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">{link.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">/{link.slug}</p>
                  </div>
                  <button
                    onClick={() => toggleSelectLink(link.slug)}
                    className={`ml-4 px-3 py-1 rounded-md text-xs font-medium border transition-colors ${selectedForDeletion.includes(link.slug) ? 'bg-red-600 text-white border-red-600 hover:bg-red-500' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                  >
                    {selectedForDeletion.includes(link.slug) ? 'Selected' : 'Select'}
                  </button>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <a
                href="/pricing"
                className="px-4 py-2 rounded-lg font-medium bg-gradient-to-r from-amber-500 to-yellow-500 text-white shadow hover:from-amber-600 hover:to-yellow-600 transition-colors"
              >
                Upgrade
              </a>
              <button
                onClick={bulkDeleteSelected}
                className={`px-4 py-2 rounded-lg font-medium text-white transition-colors ${selectedForDeletion.length >= excessCount ? 'bg-red-600 hover:bg-red-700' : 'bg-red-400 cursor-not-allowed'}`}
                disabled={selectedForDeletion.length < excessCount}
              >
                Delete {selectedForDeletion.length}/{excessCount} Required
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-4">This dialog will close automatically once you are within your plan limit.</p>
          </DialogContent>
        </Dialog>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Smart Links</h1>
              <span className={`text-xs px-2 py-1 rounded-full font-medium tracking-wide border ${planId==='pro' ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white border-transparent' : planId==='growth' ? 'bg-emerald-600/10 text-emerald-600 dark:text-emerald-400 border-emerald-600/20' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'}`}>{planName}</span>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Create trackable links and capture leads from your audience
            </p>
          </div>
          <div className="flex flex-col sm:items-end gap-2 min-w-[260px] w-full sm:w-auto">
            {loadingPlan && (
              <div className="text-xs text-gray-500 dark:text-gray-400 animate-pulse">Loading plan…</div>
            )}
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-50 dark:bg-blue-500/10 rounded-lg flex items-center justify-center">
                <Link2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {activeLinks}
                {typeof limits?.smartLinks === 'number' && (
                  <span className="text-base font-medium text-gray-500 dark:text-gray-500"> / {limits.smartLinks}</span>
                )}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                Active Links
                {activeLinks >= (limits?.smartLinks ?? Infinity) && (
                  <span className="px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs">Limit Reached</span>
                )}
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg flex items-center justify-center">
                <MousePointer className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {totalClicks.toLocaleString()}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Total Clicks
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-50 dark:bg-purple-500/10 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {totalFans.toLocaleString()}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                New Fans
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Link Creator */}
          <div className="lg:col-span-1">
          <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
              <div
 className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Create New Link</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Build a smart link to track engagement
                </p>
              </div>

              <div className="p-6 space-y-6">
                {/* Link Title */}
                <div>
                  <label htmlFor="linkTitle" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Link Title
                  </label>
                  <input
                    type="text"
                    id="linkTitle"
                    placeholder="e.g., New Single Pre-save"
                    value={linkTitle}
                    onChange={(e) => setLinkTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-white/5 dark:text-white"
                  />
                </div>

                {/* Destination URL */}
                <div>
                  <label htmlFor="destinationUrl" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Destination URL
                  </label>
                  <input
                    type="url"
                    id="destinationUrl"
                    placeholder="https://spotify.com/link"
                    value={destinationUrl}
                    onChange={(e) => setDestinationUrl(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-white/5 dark:text-white"
                  />
                </div>

                {/* Capture Settings */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">Lead Capture</h3>
                  
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-white/5 rounded-lg">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">Email Capture</h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Collect fan emails before redirect</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={requireEmail}
                        onChange={(e) => setRequireEmail(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-white/5 rounded-lg">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">Phone Capture</h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Collect phone numbers before redirect</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={requireNumber}
                        onChange={(e) => setRequireNumber(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>

                {/* Create Button */}
                <button
                  onClick={createLink}
                  disabled={!destinationUrl || !linkTitle || creating || activeLinks >= (limits?.smartLinks ?? 1)}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {creating ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  {creating ? 'Creating...' : (activeLinks >= (limits?.smartLinks ?? 1) ? 'Limit Reached' : 'Create Smart Link')}
                </button>
                {activeLinks >= (limits?.smartLinks ?? 1) && (
                  <div className="text-xs text-red-600 dark:text-red-400 mt-2 flex flex-col gap-1">
                    <span>You have reached the maximum number of smart links for your {limits.label || 'current'} plan.</span>
                    <a href="/pricing" className="underline text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300">Upgrade to unlock more</a>
                  </div>
                )}

                {/* Generated Link Success */}
                {generatedLink && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                    <h3 className="font-medium text-green-800 dark:text-green-200 mb-3">Link Created Successfully!</h3>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border">
                        <span className="text-sm text-gray-600 dark:text-gray-400 font-mono truncate">{generatedLink}</span>
                        <button
                          onClick={() => copyToClipboard(generatedLink)}
                          className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => copyToClipboard(generatedLink)}
                          className="flex-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 py-2 px-3 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center gap-2"
                        >
                          <Copy className="w-4 h-4" />
                          Copy
                        </button>

                        <Dialog>
                          <DialogTrigger asChild>
                            <button className="flex-1 bg-green-600 text-white py-2 px-3 rounded-lg font-medium hover:bg-green-700 flex items-center justify-center gap-2">
                              <QrCode className="w-4 h-4" />
                              QR Code
                            </button>
                          </DialogTrigger>
                          <DialogContent className="bg-white dark:bg-gray-800">
                            <DialogHeader>
                              <DialogTitle className="text-gray-900 dark:text-white">QR Code</DialogTitle>
                              <DialogDescription className="text-gray-600 dark:text-gray-400">
                                Scan to open this smart link
                              </DialogDescription>
                            </DialogHeader>
                            <div className="flex flex-col items-center gap-4">
                              <div className="p-4 bg-white rounded-lg">
                                <QRCode value={generatedLink} size={192} />
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 break-all text-center">{generatedLink}</p>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Links List */}
          <div className="lg:col-span-2">
          <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Your Smart Links</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Manage and track your link performance
                </p>
              </div>

              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {loadingLinks && (
                  <div className="p-6 text-center">
                    <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-gray-500 dark:text-gray-400">Loading your links...</p>
                  </div>
                )}
                
                {!loadingLinks && existingLinks.length === 0 && (
                  <div className="p-6 text-center">
                    <Link2 className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">No smart links yet</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500">Create your first link to get started</p>
                  </div>
                )}

                {processedLinks.map((link) => (
                  <div key={link.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <div className="flex items-start justify-between mb-4">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-gray-900 dark:text-white truncate">{link.title}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <a 
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-mono truncate"
                          >
                            {link.url}
                          </a>
                          <button
                            onClick={() => copyToClipboard(link.url)}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          
                          <Dialog>
                            <DialogTrigger asChild>
                              <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                <QrCode className="w-4 h-4" />
                              </button>
                            </DialogTrigger>
                            <DialogContent className="bg-white dark:bg-gray-800">
                              <DialogHeader>
                                <DialogTitle className="text-gray-900 dark:text-white">QR Code</DialogTitle>
                                <DialogDescription className="text-gray-600 dark:text-gray-400">
                                  Scan to open this smart link
                                </DialogDescription>
                              </DialogHeader>
                              <div className="flex flex-col items-center gap-4">
                                <div className="p-4 bg-white rounded-lg">
                                  <QRCode value={link.url} size={192} />
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 break-all text-center">{link.url}</p>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Created {new Date(link.created).toLocaleDateString()} • Points to: {link.destination}
                        </p>
                      </div>

                      <div className="flex items-center gap-1 ml-4">
                        <button
                          onClick={() => openEdit(link)}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setSlugToDelete(link.slug); setIsConfirmOpen(true); }}
                          disabled={deleting === link.slug}
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <MousePointer className="w-5 h-5 text-blue-600 dark:text-blue-400 mx-auto mb-1" />
                        <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{link.clicks?.toLocaleString() || '0'}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Clicks</p>
                      </div>
                      <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <Users className="w-5 h-5 text-purple-600 dark:text-purple-400 mx-auto mb-1" />
                        <p className="text-lg font-bold text-purple-600 dark:text-purple-400">+{link.newFans?.toLocaleString() || '0'}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">New Fans</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isConfirmOpen} onOpenChange={(open) => { setIsConfirmOpen(open); if (!open) setSlugToDelete(null); }}>
          <DialogContent className="bg-white dark:bg-black">
            <DialogHeader>
              <DialogTitle className="text-gray-900 dark:text-white">Delete Smart Link</DialogTitle>
              <DialogDescription className="text-gray-600 dark:text-gray-400">
                Are you sure you want to delete this smart link? This action cannot be undone and the link will stop working immediately.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setIsConfirmOpen(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                disabled={deleting && slugToDelete && deleting === slugToDelete}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={!slugToDelete || (deleting && deleting === slugToDelete)}
              >
                {deleting && slugToDelete && deleting === slugToDelete ? 'Deleting...' : 'Delete Link'}
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Page Name Dialog */}
        <Dialog open={isEditOpen} onOpenChange={(o) => { setIsEditOpen(o); if (!o) { setEditingLink(null); setNewSlug(''); setAvailable(null); } }}>
          <DialogContent className="bg-white dark:bg-black">
            <DialogHeader>
              <DialogTitle className="text-gray-900 dark:text-white">Edit Page Name</DialogTitle>
              <DialogDescription className="text-gray-600 dark:text-gray-400">
                Set a unique page name that will be used in the URL path.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <input
                  type="text"
                  value={newSlug}
                  onChange={(e) => { setNewSlug(e.target.value); setAvailable(null); }}
                  onBlur={() => checkAvailability(newSlug)}
                  placeholder="e.g. new-single"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
                {checking && <p className="text-xs text-gray-500 mt-1">Checking availability...</p>}
                {available === true && <p className="text-xs text-green-600 mt-1">Available</p>}
                {available === false && <p className="text-xs text-red-600 mt-1">Not available</p>}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setIsEditOpen(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveNewSlug}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                disabled={!newSlug}
              >
                Save Changes
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SmartLinks;